import { USE_EXTERNAL_API } from '@m/config/env';
import { LandingPageExtra, LandingPageFee } from '@m/types';
import { Vehicle } from '@m/types/api/Vehicle';
import {
  AcquisitionType,
  OnboardingFlow,
  ProfileFieldSource,
} from '@m/types/grpc/MemberOnboardingSource';
import { apiFetchV2, setAuthToken } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import UserService from 'apps/customer/services/UserService';
import { setUser, userStore } from 'apps/customer/state/user';

import type { RateBaseV2, Site, User, Validation } from '@m/types/api';
import type { AddedVia } from '@m/types/grpc/MemberOnboardingSource';
import type { APIResponse } from '@m/utils/http/types';
import type { UseMutationOptions } from '@tanstack/react-query';

export type UserSessionData = {
  userVisitState: {
    visitView: {
      extras: LandingPageExtra[];
      fees: LandingPageFee[];
      qrAction: string;
      rate: RateBaseV2 | null;
      site: Site | null;
      validation: Validation | null;
    };
  };
};

export const useUserSession = () =>
  useQuery({
    queryKey: ['user-session'],
    queryFn: () => apiFetchV2<UserSessionData>('/user/visit/state'),
    refetchOnWindowFocus: false,
  });

interface UpdateUserInfoParams {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  profileFieldSource?: ProfileFieldSource;
}

interface AddVehicleParams {
  addedVia?: AddedVia;
  color?: string;
  isTemporaryVehicle?: boolean;
  licensePlate: {
    state: { id: number };
    text: string;
  };
  makeId?: number;
  modelId?: number;
  modelYearId?: number;
  temporaryVehicleEndTime?: number;
}

export const useGetUser = () =>
  useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

export const useRequestCode = (
  options?: UseMutationOptions<APIResponse, Error, { phoneNumber: string }>,
) =>
  useMutation({
    mutationFn: ({ phoneNumber }) => UserService.requestCode({ phoneNumber }),
    ...options,
  });

export const useRequestUpdatePhoneCode = (
  options?: UseMutationOptions<APIResponse, Error, { phoneNumber: string }>,
) =>
  useMutation({
    mutationFn: async ({ phoneNumber }) => {
      const response = await UserService.requestUpdateVerificationCode({ phoneNumber });
      if (!response.success) {
        throw new Error(response.error || 'Failed to request verification code');
      }
      return response;
    },
    ...options,
  });

export const useVerifyUpdatePhoneCode = (options?: { onError?: (error: unknown) => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ phoneNumber, code }: { code: string; phoneNumber: string }) => {
      const response = await UserService.verifyUpdateCode({ phoneNumber, secret: code });
      if (!response.success) {
        throw new Error(response.error || 'Failed to verify code');
      }
      return response;
    },
    onSuccess: (response) => {
      queryClient.setQueryData(['user'], response);
      if (response.data?.user) {
        setUser({ user: response.data.user });
      }
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error) => options?.onError?.(error),
  });
};

export const useSendVerificationCode = (
  options?: UseMutationOptions<unknown, Error, { phoneNumber: string }>,
) =>
  useMutation({
    mutationFn: ({ phoneNumber }: { phoneNumber: string }) =>
      apiFetchV2('/user/verification/send', {
        method: 'POST',
        body: { phoneNumber },
      }),
    ...options,
  });

export const useVerifyAndRegister = (
  options?: UseMutationOptions<
    unknown,
    Error,
    {
      acquisitionType?: AcquisitionType;
      beginSession: boolean;
      code: string;
      onboardingFlow?: OnboardingFlow;
      phoneNumber?: string | null;
      profileFieldSource?: ProfileFieldSource;
    }
  >,
) =>
  useMutation({
    mutationFn: ({
      phoneNumber,
      code,
      beginSession,
      acquisitionType,
      onboardingFlow,
      profileFieldSource,
    }: {
      acquisitionType?: AcquisitionType;
      beginSession: boolean;
      code: string;
      onboardingFlow?: OnboardingFlow;
      phoneNumber?: string | null;
      profileFieldSource?: ProfileFieldSource;
    }) => {
      let sessionMode: 'token' | 'cookie' | undefined;
      if (USE_EXTERNAL_API) {
        sessionMode = 'token';
      } else if (beginSession) {
        sessionMode = 'cookie';
      }
      return apiFetchV2('/user/verification/register', {
        method: 'POST',
        body: {
          phoneNumber,
          secret: code,
          sessionMode,
          acquisitionType,
          onboardingFlow,
          profileFieldSource,
        },
      });
    },
    ...options,
  });

export const useUpdateUserInfo = (options?: { onError?: (error: unknown) => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateUserInfoParams) => {
      const response = await UserService.updateUserInfo(params);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update user info');
      }
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
    onError: (error) => options?.onError?.(error),
  });
};

export const useVerifyCode = (options?: { onError?: (error: unknown) => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      phoneNumber,
      code,
      beginSession,
    }: {
      beginSession: boolean;
      code: string;
      phoneNumber: string;
    }) => UserService.verifyCode({ phoneNumber, code, beginSession }),
    onSuccess: (response) => {
      if (response.success) {
        // this is for local dev
        USE_EXTERNAL_API && setAuthToken(response?.data?.token);
        queryClient.invalidateQueries({ queryKey: ['user'] });
      } else {
        throw new Error(response.error);
      }
    },
    onError: (error) => options?.onError?.(error),
  });
};

export const useRegisterUser = (options?: {
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      firstName,
      lastName,
      email,
      password,
      phoneNumber,
      agreements,
      acquisitionType,
      onboardingFlow,
      profileFieldSource,
    }: {
      acquisitionType?: AcquisitionType;
      agreements?: { id: number; type: { id: number } }[];
      email?: string;
      firstName?: string;
      lastName?: string;
      onboardingFlow?: OnboardingFlow;
      password?: string;
      phoneNumber?: string;
      profileFieldSource?: ProfileFieldSource;
    }) =>
      UserService.signUpRegister({
        firstName,
        lastName,
        email,
        password,
        phoneNumber,
        agreements,
        acquisitionType,
        onboardingFlow,
        profileFieldSource,
      }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['user'] });
        options?.onSuccess?.();
      } else {
        throw new Error(response.error);
      }
    },
    onError: (error) => options?.onError?.(error),
  });
};

export const useLogout = (options?: {
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => UserService.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error),
  });
};

export const useFetchUserVehicles = (options?: { enabled: boolean }) =>
  useQuery({
    queryKey: ['userVehicles'],
    queryFn: () => apiFetchV2<Vehicle[]>('/customer/vehicles'),
    enabled: options?.enabled,
  });

// ─── New onboarding flow hooks ───────────────────────────────────────────────

export const useUpdateEmail = (options?: UseMutationOptions<unknown, Error, { email: string }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      apiFetchV2('/customer/update-email', { method: 'PUT', body: { email } }),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      // Screens like the active visit page read the email from the effector user store, so put the
      // saved email there too or they keep asking for an email the user already added. Done
      // synchronously (not a refetch) so it's set before the caller navigates on.
      const [, { email }] = args;
      const { user } = userStore.getState();
      if (user.isRegistered) {
        setUser({ user: { ...(user as User), email } });
      }
      options?.onSuccess?.(...args);
    },
  });
};

// ─── Legacy hooks (do not modify) ────────────────────────────────────────────

export const useAddVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddVehicleParams) =>
      apiFetchV2('/customer/vehicle', { method: 'POST', body: params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userVehicles'] });
    },
  });
};

export const useVerifyPhoneUpdate = (
  options?: UseMutationOptions<unknown, Error, { phoneNumber: string; secret: string }>,
) =>
  useMutation({
    mutationFn: ({ phoneNumber, secret }: { phoneNumber: string; secret: string }) =>
      apiFetchV2('/user/verify-phone-update', {
        method: 'POST',
        body: { phoneNumber, secret },
      }),
    ...options,
  });
